/**
 * `<RxNormMedicationPicker>` — Inline RxNorm typeahead for the Plan section's
 * medication name column. Uses `medplum.valueSetExpand` against the same
 * RxNorm ValueSet (`http://cts.nlm.nih.gov/fhir/ValueSet/2.16.840.1.113762.1.4.1010.4`)
 * that the patient-chart side-menu Medications form uses (see
 * `pages/Patient/components/Medications.tsx`), so behavior stays consistent
 * across surfaces.
 *
 * Behavior
 *  - Input shows the medication display string (free-text editable).
 *  - Typing ≥ 2 chars triggers a debounced (250ms) ValueSet expansion.
 *  - Pressing a dropdown item fires `onPick({ code, display, system })` so
 *    the parent can write the structured code AND display to the row.
 *  - Typing without picking still flows through `onDisplayChange` so the
 *    free-text display stays in sync (used as fallback when the doctor
 *    types a brand name that isn't in the ValueSet — the resulting
 *    MedicationStatement will have a display but no code, which the cascade
 *    handles).
 *
 * Compact size="xs" + Combobox `withinPortal` so the dropdown floats above the
 * Mantine table cell without clipping.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Combobox, InputBase, Loader, ScrollArea, Stack, Text, useCombobox } from '@mantine/core'
import { useMedplum } from '@medplum/react'
import { logError } from '@/utils/errorUtils'
import { RXNORM_SYSTEM } from './fhirSystems'
import { CLEAR_OPTION_VALUE, ClearSelectionOption } from './ClearSelectionOption'

const RXNORM_VALUE_SET = 'http://cts.nlm.nih.gov/fhir/ValueSet/2.16.840.1.113762.1.4.1010.4'

export type RxNormPick = {
  code: string
  display: string
  system: string
}

export interface RxNormMedicationPickerProps {
  /** Current display string from form state. */
  display: string
  /** Fires on every keystroke — keeps the free-text display field in sync
   *  with the form state, so the doctor's typing isn't lost if they don't
   *  pick a code. */
  onDisplayChange: (next: string) => void
  /** Fires when the doctor picks an item from the dropdown. Caller writes
   *  both code and display to the underlying row. */
  onPick: (picked: RxNormPick) => void
  placeholder?: string
  testId?: string
}

export function RxNormMedicationPicker({
  display,
  onDisplayChange,
  onPick,
  placeholder,
  testId,
}: RxNormMedicationPickerProps) {
  const medplum = useMedplum()
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  })

  // Local search mirrors `display` so external resets (form reset, AI hydrate)
  // are reflected immediately.
  const [search, setSearch] = useState<string>(display)
  const [options, setOptions] = useState<RxNormPick[]>([])
  const [loading, setLoading] = useState(false)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    setSearch(display)
  }, [display])

  const runSearch = useCallback(
    async (q: string) => {
      const trimmed = q.trim()
      if (trimmed.length < 2) {
        setOptions([])
        return
      }
      abortRef.current?.abort()
      const ctl = new AbortController()
      abortRef.current = ctl
      setLoading(true)
      try {
        const result = await medplum.valueSetExpand(
          {
            url: RXNORM_VALUE_SET,
            filter: trimmed,
            count: 15,
          },
          { signal: ctl.signal },
        )
        if (ctl.signal.aborted) return
        const picks: RxNormPick[] = []
        for (const c of result.expansion?.contains ?? []) {
          if (c.code && !picks.some((p) => p.code === c.code)) {
            picks.push({
              code: c.code,
              display: c.display ?? c.code,
              system: c.system ?? RXNORM_SYSTEM,
            })
          }
        }
        setOptions(picks)
      } catch (err) {
        if ((err as Error)?.name !== 'AbortError') {
          logError('progress-note-edit', 'RxNorm valueSetExpand failed', err)
        }
      } finally {
        if (!ctl.signal.aborted) setLoading(false)
      }
    },
    [medplum],
  )

  // Debounce the ValueSet expansion so we don't fire one request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      runSearch(search)
    }, 250)
    return () => clearTimeout(t)
  }, [search, runSearch])

  return (
    <Combobox
      store={combobox}
      withinPortal
      width={380}
      position="bottom-start"
      onOptionSubmit={(submittedCode) => {
        if (submittedCode === CLEAR_OPTION_VALUE) {
          // Empty pick = clear; consumer writes empty code/display to the row.
          onPick({ code: '', display: '', system: '' })
          setSearch('')
          combobox.closeDropdown()
          return
        }
        const picked = options.find((o) => o.code === submittedCode)
        if (picked) {
          onPick(picked)
          setSearch(picked.display)
        }
        combobox.closeDropdown()
      }}
    >
      <Combobox.Target>
        <InputBase
          size="xs"
          value={search}
          onChange={(e) => {
            const next = e.currentTarget.value
            setSearch(next)
            onDisplayChange(next)
            combobox.openDropdown()
            combobox.updateSelectedOptionIndex()
          }}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => combobox.closeDropdown()}
          placeholder={placeholder}
          rightSection={loading ? <Loader size={12} /> : <Combobox.Chevron />}
          rightSectionPointerEvents="none"
          data-testid={testId}
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          {display && <ClearSelectionOption />}
          <ScrollArea.Autosize mah={280} type="scroll">
            {options.length === 0 ? (
              <Combobox.Empty>
                {loading
                  ? 'Searching RxNorm…'
                  : search.trim().length < 2
                    ? 'Type at least 2 characters…'
                    : 'No matches'}
              </Combobox.Empty>
            ) : (
              options.map((o) => (
                <Combobox.Option value={o.code} key={o.code}>
                  <Stack gap={0}>
                    <Text size="sm" fw={500}>
                      {o.display}
                    </Text>
                    <Text size="xs" c="dimmed">
                      RxNorm {o.code}
                    </Text>
                  </Stack>
                </Combobox.Option>
              ))
            )}
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}
